import html2canvas from "html2canvas";
import {store} from "../../../../helper/store";

/** `.ol-scale-line` in style.css and ol.css; ScaleBarOSD sits outside the captured element, so it is drawn here. */
const OL_SCALE_LINE = {
    background: 'rgba(51, 4, 112, 0.9)',
    color: '#ffffff',
    bottom: 10,
    fontSize: 10,
    radius: 4,
    padding: 2,
    margin: 1,
    // moveScaleLineToLeft makes room for the barcode
    barcodeShift: 150,
};

const scaleLineLeftRatio = () => {
    if (window.innerWidth <= 450) return 0.72;
    if (window.innerWidth <= 850) return 0.8;
    return 0.9;
};

const osdViewersIn = (root) => {
    const mapsState = store.getState().mapsStateReducer || {};
    return Object.keys(mapsState)
        .map(mapId => ({mapState: mapsState[mapId], viewer: mapsState[mapId]?.slidemap}))
        .filter(({viewer}) => viewer?._isOpenSeadragon && viewer.element && root.contains(viewer.element));
};

const isOsdDrawn = (el, viewers) => viewers.some(({viewer}) =>
    el === viewer.drawer?.canvas
    || el === viewer.navigator?.element
    || (el.classList?.contains('morphle-tiled-fields-canvas') && viewer.container?.contains(el)));

const CLEAR_BACKGROUND_ATTR = 'data-osd-capture-clear';

/** The viewer hosts and their ancestors up to `root`; their backgrounds would cover the OSD pixels drawn underneath. */
const backgroundPath = (root, viewers) => {
    const path = new Set();
    viewers.forEach(({viewer}) => {
        for (let el = viewer.element; el && el !== root.parentElement; el = el.parentElement) path.add(el);
    });
    return [...path];
};

const relativeRect = (el, rootRect, scale) => {
    const r = el.getBoundingClientRect();
    return {
        x: (r.left - rootRect.left) * scale,
        y: (r.top - rootRect.top) * scale,
        w: r.width * scale,
        h: r.height * scale,
    };
};

const drawViewerPixels = (ctx, viewer, rootRect, scale) => {
    const host = relativeRect(viewer.element, rootRect, scale);
    ctx.save();
    ctx.beginPath();
    ctx.rect(host.x, host.y, host.w, host.h);
    ctx.clip();
    ctx.fillStyle = window.getComputedStyle(viewer.element).backgroundColor;
    ctx.fillRect(host.x, host.y, host.w, host.h);
    const canvases = [viewer.drawer?.canvas, viewer.container?.querySelector('.morphle-tiled-fields-canvas')];
    canvases.forEach(canvas => {
        if (!canvas?.width || !canvas.height || window.getComputedStyle(canvas).display === 'none') return;
        const {x, y, w, h} = relativeRect(canvas, rootRect, scale);
        ctx.drawImage(canvas, x, y, w, h);
    });
    ctx.restore();
};

const drawScaleLine = (ctx, viewer, mapState, rootRect, scale, shiftForBarcode) => {
    const {scaleText, scaleBarWidth} = mapState || {};
    if (!scaleText || !scaleBarWidth) return;
    const {fontSize, padding, margin, radius, bottom} = OL_SCALE_LINE;
    const host = relativeRect(viewer.element, rootRect, scale);

    ctx.save();
    ctx.font = `${fontSize * scale}px sans-serif`;
    const innerW = Math.max(scaleBarWidth, ctx.measureText(scaleText).width / scale + 4);
    const textH = fontSize * 1.2;
    const boxW = (innerW + 2 + 2 * margin + 2 * padding) * scale;
    const boxH = (textH + 1 + 2 * margin + 2 * padding) * scale;
    const available = host.w - (shiftForBarcode ? OL_SCALE_LINE.barcodeShift * scale : 0);
    const left = Math.max(8 * scale, Math.min(available * scaleLineLeftRatio(), host.w - boxW - 8 * scale));
    const boxX = host.x + left;
    const boxY = host.y + host.h - bottom * scale - boxH;

    ctx.fillStyle = OL_SCALE_LINE.background;
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(boxX, boxY, boxW, boxH, radius * scale);
    else ctx.rect(boxX, boxY, boxW, boxH);
    ctx.fill();

    const innerX = boxX + (padding + margin) * scale;
    const innerY = boxY + (padding + margin) * scale;
    const innerWpx = innerW * scale;
    ctx.fillStyle = OL_SCALE_LINE.color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(scaleText, innerX + innerWpx / 2, innerY + 0.1 * fontSize * scale);

    // `.ol-scale-line-inner` has a border on every side but the top
    const bottomY = innerY + textH * scale;
    ctx.strokeStyle = OL_SCALE_LINE.color;
    ctx.lineWidth = scale;
    ctx.beginPath();
    ctx.moveTo(innerX, innerY);
    ctx.lineTo(innerX, bottomY);
    ctx.lineTo(innerX + innerWpx, bottomY);
    ctx.lineTo(innerX + innerWpx, innerY);
    ctx.stroke();
    ctx.restore();
};

/**
 * html2canvas for `root`, with OpenSeadragon viewers inside it composited from their own canvases
 * (slide, tiled fields) underneath the html2canvas layer (annotations, watermark, labels). The
 * navigator is left out, as the OpenLayers screenshot has no minimap either.
 * Resolves to a canvas; without OpenSeadragon viewers this is plain html2canvas.
 */
export const captureElement = async (root, h2cOptions = {}, {includeScaleLine = true, shiftScaleLine = false} = {}) => {
    const viewers = osdViewersIn(root);
    if (!viewers.length) return html2canvas(root, h2cOptions);

    const path = backgroundPath(root, viewers);
    path.forEach(el => el.setAttribute(CLEAR_BACKGROUND_ATTR, ''));
    let layer;
    try {
        layer = await html2canvas(root, {
            ...h2cOptions,
            backgroundColor: null,
            ignoreElements: el => isOsdDrawn(el, viewers),
            onclone: clonedDoc => clonedDoc.querySelectorAll(`[${CLEAR_BACKGROUND_ATTR}]`)
                .forEach(el => el.style.setProperty('background', 'transparent', 'important')),
        });
    } finally {
        path.forEach(el => el.removeAttribute(CLEAR_BACKGROUND_ATTR));
    }
    const rootRect = root.getBoundingClientRect();
    const scale = layer.width / Math.max(1, rootRect.width);

    const out = document.createElement('canvas');
    out.width = layer.width;
    out.height = layer.height;
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out.width, out.height);
    viewers.forEach(({viewer}) => drawViewerPixels(ctx, viewer, rootRect, scale));
    ctx.drawImage(layer, 0, 0);
    if (includeScaleLine) {
        viewers.forEach(({viewer, mapState}) => drawScaleLine(ctx, viewer, mapState, rootRect, scale, shiftScaleLine));
    }
    return out;
};

const isIdle = (viewer) => {
    const viewport = viewer.viewport;
    if (!viewport) return true;
    const settled = Math.abs(viewport.getZoom(true) - viewport.getZoom()) < 1e-9
        && viewport.getCenter(true).distanceTo(viewport.getCenter()) < 1e-9
        && Math.abs(viewport.getRotation(true) - viewport.getRotation()) < 1e-6;
    if (!settled) return false;
    const world = viewer.world;
    for (let i = 0; i < world.getItemCount(); i++) {
        if (!world.getItemAt(i).getFullyLoaded()) return false;
    }
    return true;
};

/** Resolves once the viewport has stopped moving and the visible tiles are drawn, or after `timeoutMs` (missing tiles never load). */
export const waitForOsdIdle = (viewer, {timeoutMs = 4000, pollMs = 100} = {}) => new Promise(resolve => {
    const start = Date.now();
    const tick = () => {
        if (isIdle(viewer) || Date.now() - start > timeoutMs) {
            requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
            return;
        }
        setTimeout(tick, pollMs);
    };
    tick();
});
